import React, { useCallback, useEffect, Suspense } from 'react';
import { prop, identity, pick } from 'ramda';
import { clsx } from 'js/app/utils/clsx.util';
import { useDispatch } from 'react-redux';

import { Login } from 'js/components/main/Login';
import { ManagedPopup } from 'js/components/common/Popup';
import { WebContentInner } from 'js/components/system/WebContent';

import { BACKBONE_APP_ROOT } from 'js/backbone/constants';
import { scrollService } from 'js/services/scroll.service';
import { setActionAfterLogin } from 'js/hooks/user/store/buzz/actions';
import { usePortraitOrientation } from 'js/app/hooks/device/hooks/useMediaQuery';
import { useCombinedOnClose, useOnClose } from 'js/components/common/PopupManager';
import { useCheckOccupation } from '@interlayer/js/app/hooks/user/hooks/useCheckOccupation';
import { useAppSelector, useUserSelector, useDeviceSelector, useAuthSelector } from 'js/store/hooks';

import style from './LoginPopup.scss';
import { BlockInfo } from '@interlayer/js/app/components/main/Registration/components/common/BlockInfo';
import { useTranslations } from '@core/js/hooks/i18n';
import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';

const popupClassNames = {
    inner: style.popupInner
};

const layerClassNames = {
    layout: style.layout,
    layer: style.layer
};

export const LoginPopup = ({ id, title = 'Log in', onClose, onLogin = identity, controls }) => {
    const { isLoggedIn, userDetails } = useUserSelector(pick(['isLoggedIn', 'userDetails']));
    const loginWebContent = useAppSelector(prop('loginWebContent'));
    const closePopup = useCombinedOnClose(onClose, id);
    const isLoginInProcess = useAuthSelector(prop('isLoginInProcess'));
    const dispatch = useDispatch();
    const checkOccupation = useCheckOccupation();
    const t = useTranslations('playtech.mobile.system.login.');
    const loginInfoConfig = useConfigTag(CONFIG_TAG_TYPE.loginInfoConfig)?.config;

    const clearClosePopup = useOnClose(id);
    const isMobile = useDeviceSelector(prop('isMobile'));
    const isLandscape = !usePortraitOrientation();

    useEffect(() => {
        if (!isLoggedIn || isLoginInProcess) {
            return;
        }

        clearClosePopup();
        scrollService.scrollTo(0);
    }, [clearClosePopup, isLoggedIn, onLogin, userDetails, isLoginInProcess]);

    const onLoginActions = useCallback((userData, sessionTokenAfterLogin, playerTags) => {
        onLogin(userData, sessionTokenAfterLogin, playerTags);
        checkOccupation({ playerTags });
    }, [checkOccupation, onLogin]);

    useEffect(() => {
        dispatch(setActionAfterLogin(onLoginActions));
    }, [dispatch, onLoginActions]);

    return (
        <ManagedPopup
            id={id}
            title={title}
            controls={controls}
            onClose={closePopup}
            className={clsx(style.container, { [style.landscapeHorizontal]: isMobile && isLandscape })}
            classNames={popupClassNames}
            layerClassNames={layerClassNames}
            fullscreen
            isCustomLogin
        >
            <BlockInfo
                t={t}
                tKey="info"
                className={style.infoBlock}
            />

            {loginInfoConfig && (
                <div
                    style={{ backgroundColor: loginInfoConfig.backgroundColor }}
                    className={clsx(style.loginInfoBlock, { [style.withoutBg]: loginInfoConfig.isLogoIconsEnabled })}
                >
                    {loginInfoConfig.isLogoIconsEnabled && (
                        <div className={clsx(style.loginInfoLogos, { [style.reverse]: loginInfoConfig.isLogoIconsEnabled })}>
                            {loginInfoConfig.isBBEnabled && (
                                <span className={clsx(style.logoIcon, style.logoBingo)} />
                            )}

                            {loginInfoConfig.isBCEnabled && (
                                <span className={clsx(style.logoIcon, style.logoCasino)} />
                            )}
                        </div>
                    )}

                    <span
                        style={{ color: loginInfoConfig.textColor }}
                        className={clsx(style.loginInfoText, { [style.defaultColor]: loginInfoConfig.isLogoIconsEnabled })}
                    >
                        {t('loginInfoText')}
                    </span>

                    {!loginInfoConfig.isLogoIconsEnabled && (
                        <div className={style.loginInfoLogos}>
                            {loginInfoConfig.isBBEnabled && (
                                <SvgIcon
                                    type="buzz-bingo"
                                    className={style.loginInfoLogo}
                                    color={loginInfoConfig.logoColor || 'white-const-color'}
                                />
                            )}

                            {loginInfoConfig.isBCEnabled && (
                                <SvgIcon
                                    type="buzz-casino"
                                    className={style.loginInfoLogo}
                                    color={loginInfoConfig.logoColor || 'white-const-color'}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}

            <Suspense fallback="">
                <Login
                    closePopup={closePopup}
                    className={`${style.loginPopupContainer} ${BACKBONE_APP_ROOT}`}
                />
            </Suspense>

            {loginWebContent && (
                <>
                    <span className={style.delimiter}/>
                    <WebContentInner
                        popupId={id}
                        content={loginWebContent}
                        className={`${style.loginWc} ${BACKBONE_APP_ROOT}`}
                    />
                </>
            )}
        </ManagedPopup>
    );
};
